import React, { useState } from 'react';

import PropTypes from 'prop-types';
import styled from 'styled-components';

import { mobile } from '~/techstyle-shared/react-components';
import { FormattedMessage } from '~/techstyle-shared/react-intl';

import { Header } from '../../../styles/header';
import { Paragraph } from '../../../styles/paragraph';

const IMAGE_TOO_LARGE = 'IMAGE_TOO_LARGE';
const IMPROPER_FILE = 'IMPROPER_FILE';
const FILE_ERROR = 'FILE_ERROR';
const MAX_FILE_SIZE_IN_MB = 5;
const MB_SIZE = 1024 * 1024;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 14px 0px;
`;

const Title = styled(Header).attrs({
  level: 1,
})`
  font-size: 16px;
  font-weight: 600;
  text-align: left;
  padding-top: 20px;
  padding-bottom: 12px;
`;

const InstructionsList = styled.ul`
  margin-top: 0px;
  margin-left: 16px;
  font-size: 12px;
  list-style-type: disc;
`;

const InstructionsItem = styled.li`
  margin: 6px 0px;
  font-size: 14px;
  line-height: 2;
`;
const HiddenInput = styled.input`
  display: none;
`;

const PictureWrapper = styled.div`
  position: relative;
  width: 10%;
  margin-bottom: 10px;

  &:before {
    content: '';
    display: block;
    padding-top: ${100 * (1 / 1)}%; /* Aspect ratio */
    width: 100%;
  }
`;

const Picture = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;

  background-image: ${(props) => `url("${props.bg}")`};
  background-position: center center;
  background-size: cover;
`;

const Pix = styled.label`
  font-family: inherit;
  font-size: 14px;
  cursor: pointer;
  text-align: left;
  height: 50px;
  width: calc(50% - 16px);
  background-color: transparent;
  color: ${({ theme }) => theme.colors.textDefault};
  border: solid 3px ${({ theme }) => theme.colors.palette.black};
  font-weight: bold;
  border-radius: 3px;
  padding: 12px 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.16s ease-in-out, border-color 0.16s ease-in-out;

  &:active,
  &:hover,
  &:focus {
    background-color: ${({ theme }) => theme.colors.palette.black};
    color: ${({ theme }) => theme.colors.textLight};
  }

  ${mobile`
    width:100%;
  `};
`;

const RedMessage = styled(Paragraph).attrs({ size: 12 })`
  font-weight: normal;
  color: ${({ theme }) => theme.colors.error};
  margin-bottom: 10px;
`;

function ImageErrorMessage({ error }) {
  if (error === IMAGE_TOO_LARGE) {
    return (
      <RedMessage>
        <FormattedMessage
          defaultMessage="Image file size too large! It must be less than 5MB."
          id="site_account_reviews.image_too_large_error"
        />
      </RedMessage>
    );
  } else if (error === IMPROPER_FILE) {
    return (
      <RedMessage>
        <FormattedMessage
          defaultMessage="That's not the right file type! Please select an image!"
          id="site_account_reviews.file_not_an_image"
        />
      </RedMessage>
    );
  } else if (error === FILE_ERROR) {
    return (
      <RedMessage>
        <FormattedMessage
          defaultMessage="An error occurred when uploading your image. Please try another."
          id="site_account_reviews.file_error"
        />
      </RedMessage>
    );
  }
  return null;
}

ImageErrorMessage.propTypes = {
  error: PropTypes.string,
};

const ReviewOptionImage = ({ className, onChange }) => {
  const [photoPath, setPhotoPath] = useState(null);

  const fileChangedHandler = (event) => {
    const input = event.target;
    const size = input.files[0].size;

    if (size > MAX_FILE_SIZE_IN_MB * MB_SIZE) {
      setPhotoPath({ error: IMAGE_TOO_LARGE });
    } else {
      const reader = new FileReader();

      reader.onloadend = (fileEvent) => {
        let photoPath;
        if (fileEvent.target.error) {
          setPhotoPath({ error: FILE_ERROR, photoPath: null });
        } else if (fileEvent.target.readyState === FileReader.DONE) {
          photoPath = fileEvent.target.result;

          const fileType = photoPath.substr(0, photoPath.indexOf(';'));
          if (fileType !== 'data:image/png' && fileType !== 'data:image/jpeg') {
            setPhotoPath({ error: IMPROPER_FILE, photoPath: null });
            return;
          }

          setPhotoPath({ photoPath, error: null });
          if (onChange) {
            onChange(photoPath);
          }
        }
      };
      reader.readAsDataURL(input.files[0]);
    }
  };

  return (
    <Wrapper className={className}>
      <Title>
        <FormattedMessage
          id="site_account_reviews.have_a_photo_optional"
          defaultMessage="Have a photo? Share it (optional)"
        />
      </Title>
      {photoPath?.photoPath && (
        <PictureWrapper>
          <Picture bg={photoPath.photoPath} />
        </PictureWrapper>
      )}
      {photoPath?.error && <ImageErrorMessage error={photoPath.error} />}
      <Pix
        htmlFor="file-upload"
        hasImage={photoPath?.photoPath ? photoPath.photoPath : false}
        type="file"
        accept=".jpg,.jpeg,.png,image/jpeg,image/png"
      >
        {photoPath?.photoPath ? (
          <FormattedMessage
            defaultMessage="Choose a different photo"
            id="site_account_reviews.choose_different_photo"
          />
        ) : (
          <FormattedMessage
            defaultMessage="ADD PHOTO"
            id="site_account_reviews.add_photo"
          />
        )}
      </Pix>
      <HiddenInput
        id="file-upload"
        type="file"
        accept="image/*"
        onChange={fileChangedHandler}
      />

      <InstructionsList>
        <InstructionsItem key={1}>
          <FormattedMessage
            defaultMessage="Max file size {maxSize} MB and must be in PNG or JPEG format."
            id="site_account_reviews.image_instructions_1"
            values={{ maxSize: MAX_FILE_SIZE_IN_MB }}
          />
        </InstructionsItem>
        <InstructionsItem key={2}>
          <FormattedMessage
            defaultMessage="We recommend using a square image."
            id="site_account_reviews.image_instructions_2"
          />
        </InstructionsItem>
        <InstructionsItem key={3}>
          <FormattedMessage
            defaultMessage="Submissions should include the product you're reviewing."
            id="site_account_reviews.image_instructions_3"
          />
        </InstructionsItem>
      </InstructionsList>
    </Wrapper>
  );
};

ReviewOptionImage.propTypes = {
  className: PropTypes.string,
  onChange: PropTypes.func.isRequired,
};

export default ReviewOptionImage;
